import React, { useState, useEffect, useRef, useCallback } from 'react';
import axiosInstance from '../utils/axiosInstance';
import {
  FaFingerprint, FaCheckCircle, FaTimesCircle, FaClock,
  FaSpinner, FaCalendarDay, FaChevronLeft, FaChevronRight,
  FaServer, FaUserCheck, FaExclamationTriangle,
  FaIdCard, FaBan, FaShieldAlt, FaArrowUp, FaFilter, FaSync,
  FaSearch, FaCopy, FaCheck
} from 'react-icons/fa';
import dayjs from 'dayjs';
import 'dayjs/locale/tr';
dayjs.locale('tr');

const C = {
  bg:'#0b0c10', surface:'#111318', card:'#161920',
  border:'#1e2230', hover:'#1a1d28',
  text:'#e8eaf0', sub:'#8b90a0', dim:'#4a4f65', faint:'#2d3048',
  blue:'#3b82f6', green:'#10b981', red:'#ef4444', amber:'#f59e0b',
  purple:'#8b5cf6', cyan:'#06b6d4', orange:'#f97316',
};

const ST = {
  pending:          { l:'Sırada',        c:C.dim,    ic:FaClock },
  processing:       { l:'İşleniyor',     c:C.amber,  ic:FaSpinner },
  already_verified: { l:'Zaten Doğru',   c:C.cyan,   ic:FaUserCheck },
  skipped_non_try:  { l:'TRY Dışı',      c:C.dim,    ic:FaBan },
  invalid_tckn:     { l:'Geçersiz TCKN', c:C.orange, ic:FaIdCard },
  missing_data:     { l:'Eksik Bilgi',    c:C.orange, ic:FaExclamationTriangle },
  verified_success: { l:'Doğrulandı',    c:C.green,  ic:FaCheckCircle },
  verified_failed:  { l:'Başarısız',      c:C.red,    ic:FaTimesCircle },
  error:            { l:'Hata',          c:C.red,    ic:FaExclamationTriangle },
};

const UPD = {
  not_needed:{ l:'—', c:C.faint }, pending:{ l:'Bekliyor', c:C.amber },
  success:{ l:'Güncellendi', c:C.green }, failed:{ l:'Başarısız', c:C.red },
};

const CSS = `
  .vm-scroll::-webkit-scrollbar{width:4px}
  .vm-scroll::-webkit-scrollbar-track{background:transparent}
  .vm-scroll::-webkit-scrollbar-thumb{background:#2d3048;border-radius:4px}
  .vm-scroll::-webkit-scrollbar-thumb:hover{background:#4a4f65}
  @keyframes vmPulse{0%,100%{opacity:.4;transform:scale(.8)}50%{opacity:1;transform:scale(1.2)}}
  @keyframes vmSpin{to{transform:rotate(360deg)}}
  @keyframes vmFade{from{opacity:0;transform:translateY(6px)}to{opacity:1;transform:none}}
`;

/* İsim düzelt: "Güleç Taner" → "Taner Güleç" */
const fixName = (raw) => {
  if (!raw) return '—';
  const p = raw.trim().split(/\s+/);
  if (p.length < 2) return raw;
  return p.slice(1).join(' ') + ' ' + p[0];
};

/* Kopyalanabilir login */
const CopyLogin = ({ login }) => {
  const [copied, setCopied] = useState(false);
  const copy = (e) => {
    e.stopPropagation();
    navigator.clipboard.writeText(login);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };
  return (
    <span onClick={copy} title="Kopyala" style={{ display:'inline-flex', alignItems:'center', gap:4, cursor:'pointer', color:C.blue, fontWeight:600, fontSize:12, transition:'color .15s' }}>
      {login}
      {copied
        ? <FaCheck size={9} style={{ color:C.green }} />
        : <FaCopy size={9} style={{ opacity:.3, transition:'opacity .15s' }} />
      }
    </span>
  );
};

/* Stat kart */
const Stat = ({ icon:Ic, label, value, c }) => (
  <div style={{ display:'flex', alignItems:'center', gap:9, padding:'10px 13px', background:C.card, borderRadius:9, border:`1px solid ${C.border}`, flex:'1 1 100px', minWidth:100 }}>
    <div style={{ width:28, height:28, borderRadius:7, background:`${c}0d`, display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
      <Ic size={12} color={c} />
    </div>
    <div>
      <div style={{ fontSize:16, fontWeight:800, color:c, lineHeight:1, fontVariantNumeric:'tabular-nums' }}>{value}</div>
      <div style={{ fontSize:8, color:C.sub, fontWeight:600, textTransform:'uppercase', letterSpacing:.5, marginTop:1 }}>{label}</div>
    </div>
  </div>
);

const Pill = ({ c, children, pulse }) => (
  <span style={{ display:'inline-flex', alignItems:'center', gap:4, fontSize:9.5, fontWeight:700, padding:'2px 8px', borderRadius:5, background:`${c}15`, color:c, border:`1px solid ${c}20`, letterSpacing:.3, textTransform:'uppercase', whiteSpace:'nowrap' }}>
    {pulse && <span style={{ width:4, height:4, borderRadius:'50%', background:c, animation:'vmPulse 2s infinite' }} />}
    {children}
  </span>
);

const FilterChip = ({ active, label, c, count, onClick }) => (
  <button onClick={onClick} style={{ padding:'3px 9px', borderRadius:5, cursor:'pointer', fontSize:9.5, fontWeight:700, display:'flex', alignItems:'center', gap:3, background:active?`${c}15`:'transparent', color:active?c:C.dim, border:`1px solid ${active?c+'25':'transparent'}`, outline:'none', transition:'all .12s' }}>
    {label}{count>0&&<span style={{ opacity:.5 }}>({count})</span>}
  </button>
);

const SearchBox = ({ value, onChange, placeholder, width }) => (
  <div style={{ position:'relative' }}>
    <FaSearch size={9} style={{ position:'absolute', left:8, top:'50%', transform:'translateY(-50%)', color:C.dim }} />
    <input value={value} onChange={e=>onChange(e.target.value)} placeholder={placeholder||'Ara...'}
      style={{ padding:'5px 8px 5px 24px', borderRadius:6, border:`1px solid ${C.border}`, background:C.surface, color:C.text, fontSize:11, width:width||140, outline:'none' }} />
  </div>
);

/* ═══ LOG TABLOSU ═══ */
const LogTable = ({ logs, searchTerm, maxH }) => {
  const filtered = searchTerm
    ? logs.filter(l => (l.login||'').toLowerCase().includes(searchTerm.toLowerCase()) || fixName(l.name).toLowerCase().includes(searchTerm.toLowerCase()))
    : logs;

  return (
    <div className="vm-scroll" style={{ maxHeight:maxH||'none', overflowY:maxH?'auto':'visible' }}>
      <table style={{ width:'100%', borderCollapse:'collapse' }}>
        <thead>
          <tr style={{ background:C.surface }}>
            {['#','Müşteri','Login','Durum','Güncelleme','Süre'].map((h,i) => (
              <th key={i} style={{ padding:'9px 12px', textAlign:'left', color:C.sub, fontWeight:700, fontSize:9, textTransform:'uppercase', letterSpacing:.7, borderBottom:`1px solid ${C.border}`, position:'sticky', top:0, zIndex:2, background:C.surface }}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {filtered.map((log, i) => {
            const s = ST[log.status] || ST.error;
            const u = UPD[log.updateStatus] || UPD.not_needed;
            const Ic = s.ic;
            const name = fixName(log.name);
            return (
              <tr key={log._id||i}
                style={{ borderBottom:`1px solid ${C.border}10`, transition:'background .1s' }}
                onMouseEnter={e=>e.currentTarget.style.background=C.hover}
                onMouseLeave={e=>e.currentTarget.style.background='transparent'}>

                {/* # */}
                <td style={{ padding:'10px 12px', color:C.dim, fontSize:11, fontVariantNumeric:'tabular-nums', width:36 }}>{i+1}</td>

                {/* Müşteri — isim belirgin */}
                <td style={{ padding:'10px 12px' }}>
                  <div style={{ fontSize:13, fontWeight:700, color:C.text, lineHeight:1.2, letterSpacing:-.1 }}>{name}</div>
                  <div style={{ fontSize:10, color:C.dim, marginTop:2 }}>{log.currency||'—'} · ID: {log.customerID||'—'}</div>
                </td>

                {/* Login — kopyalanabilir */}
                <td style={{ padding:'10px 12px' }}>
                  <CopyLogin login={log.login||'—'} />
                </td>

                {/* Durum */}
                <td style={{ padding:'10px 12px' }}>
                  <span style={{ display:'inline-flex', alignItems:'center', gap:4, color:s.c, fontWeight:600, fontSize:11 }}>
                    <Ic size={10} style={log.status==='processing'?{animation:'vmSpin 1s linear infinite'}:{}} />
                    {s.l}
                  </span>
                </td>

                {/* Güncelleme */}
                <td style={{ padding:'10px 12px', color:u.c, fontSize:10.5, fontWeight:600 }}>{u.l}</td>

                {/* Süre */}
                <td style={{ padding:'10px 12px', color:C.dim, fontVariantNumeric:'tabular-nums', fontSize:11, width:55 }}>
                  {log.durationMs ? `${(log.durationMs/1000).toFixed(1)}s` : '—'}
                </td>
              </tr>
            );
          })}
          {filtered.length===0 && (
            <tr><td colSpan={6} style={{ padding:50, textAlign:'center', color:C.dim, fontSize:13 }}>Kayıt bulunamadı</td></tr>
          )}
        </tbody>
      </table>
    </div>
  );
};

/* ═══════════════════════════════════════
   ANA BİLEŞEN — 2 tab: Canlı / Günlük
   ═══════════════════════════════════════ */
const VerificationMonitor = () => {
  const [live, setLive] = useState(null);
  const [daily, setDaily] = useState(null);
  const [date, setDate] = useState(dayjs().format('YYYY-MM-DD'));
  const [tab, setTab] = useState('live');
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [refreshing, setRefreshing] = useState(false);
  const timerRef = useRef(null);

  const fetchLive = useCallback(async () => {
    try { const { data } = await axiosInstance.get('/verification/live'); setLive(data); } catch(_){}
  }, []);

  const fetchDaily = useCallback(async (d) => {
    setRefreshing(true);
    try { const { data } = await axiosInstance.get(`/verification/daily?date=${d}`); setDaily(data); } catch(_){}
    setRefreshing(false);
  }, []);

  useEffect(() => {
    fetchLive();
    timerRef.current = setInterval(fetchLive, 3000);
    return () => clearInterval(timerRef.current);
  }, [fetchLive]);

  useEffect(() => {
    if (tab === 'daily') fetchDaily(date);
  }, [tab, date, fetchDaily]);

  const isToday = date === dayjs().format('YYYY-MM-DD');
  const changeDate = (dir) => { setDate(prev => dayjs(prev).add(dir,'day').format('YYYY-MM-DD')); setFilter('all'); };

  // Live
  const isActive = live?.active;
  const session = isActive ? live?.session : live?.lastSession;
  const liveLogs = live?.logs || [];
  const progress = session?.progress || { pending:0, processing:0, completed:0 };
  const total = session?.totalCustomers || 0;

  // Daily
  const ds = daily?.daySummary;
  const dailyLogs = daily?.logs || [];
  const filteredLogs = filter==='all' ? dailyLogs : dailyLogs.filter(l=>l.status===filter);

  const FILTERS = [
    { k:'all',              l:'Tümü',          c:C.blue },
    { k:'verified_success', l:'Doğrulandı',    c:C.green },
    { k:'verified_failed',  l:'Başarısız',      c:C.red },
    { k:'already_verified', l:'Zaten Doğru',   c:C.cyan },
    { k:'invalid_tckn',     l:'Geçersiz TCKN', c:C.orange },
    { k:'missing_data',     l:'Eksik Bilgi',    c:C.orange },
    { k:'error',            l:'Hata',          c:C.red },
    { k:'processing',       l:'İşleniyor',     c:C.amber },
    { k:'pending',          l:'Sırada',        c:C.dim },
  ];

  return (
    <div style={{ padding:'20px 24px', background:C.bg, minHeight:'100vh', color:C.text, fontFamily:"'Segoe UI',-apple-system,sans-serif" }}>
      <style>{CSS}</style>

      {/* ═══ HEADER ═══ */}
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:18, flexWrap:'wrap', gap:10 }}>
        <div style={{ display:'flex', alignItems:'center', gap:12 }}>
          <div style={{ width:36, height:36, borderRadius:10, background:`${C.purple}12`, border:`1px solid ${C.purple}25`, display:'flex', alignItems:'center', justifyContent:'center' }}>
            <FaFingerprint size={16} color={C.purple} />
          </div>
          <div>
            <h1 style={{ margin:0, fontSize:16, fontWeight:700, color:C.text, letterSpacing:-.3 }}>Kimlik Doğrulama</h1>
            <span style={{ fontSize:10, color:C.sub }}>Doğrulama monitörü</span>
          </div>
          {isActive ? <Pill c={C.green} pulse>Aktif</Pill> : <Pill c={C.dim}>Bekleme</Pill>}
        </div>

        <div style={{ display:'flex', background:C.card, borderRadius:8, padding:3, border:`1px solid ${C.border}` }}>
          {[
            { k:'live',  l:'Canlı',  ic:FaServer },
            { k:'daily', l:'Günlük', ic:FaCalendarDay },
          ].map(t => (
            <button key={t.k} onClick={() => { setTab(t.k); setSearch(''); }} style={{
              display:'flex', alignItems:'center', gap:5, padding:'6px 16px', borderRadius:6,
              border:'none', cursor:'pointer', fontSize:11, fontWeight:600, transition:'all .15s',
              background:tab===t.k?C.blue:'transparent', color:tab===t.k?'#fff':C.dim,
            }}>
              <t.ic size={11} /> {t.l}
            </button>
          ))}
        </div>
      </div>

      {/* ═══ CANLI ═══ */}
      {tab==='live' && (
        <div style={{ animation:'vmFade .25s ease' }}>
          {session ? (<>
            {/* Progress bar */}
            <div style={{ background:C.card, borderRadius:12, padding:'14px 18px', marginBottom:14, border:`1px solid ${C.border}` }}>
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:10 }}>
                <div style={{ display:'flex', alignItems:'center', gap:7 }}>
                  {isActive && <FaSpinner size={11} style={{ color:C.amber, animation:'vmSpin 1.5s linear infinite' }} />}
                  <span style={{ fontSize:12, fontWeight:700, color:C.text }}>{isActive ? 'Havuz Çalışıyor' : 'Son Oturum'}</span>
                  <span style={{ fontSize:10, color:C.dim }}>
                    {dayjs(session.startedAt||session.olusturmaTarihi).format('HH:mm:ss')}
                    {session.completedAt && ` → ${dayjs(session.completedAt).format('HH:mm:ss')}`}
                  </span>
                </div>
                <span style={{ fontSize:12, fontWeight:700, color:C.text }}>{progress.completed}<span style={{ color:C.dim }}>/{total}</span></span>
              </div>
              <div style={{ display:'flex', height:5, borderRadius:3, overflow:'hidden', background:C.faint }}>
                <div style={{ width:`${total?(progress.completed/total)*100:0}%`, background:C.green, transition:'width .5s' }} />
                <div style={{ width:`${total?(progress.processing/total)*100:0}%`, background:C.amber, transition:'width .5s' }} />
              </div>
              <div style={{ display:'flex', gap:14, marginTop:8, fontSize:10, color:C.dim }}>
                <span>Sırada: <b style={{ color:C.text }}>{progress.pending}</b></span>
                <span>İşleniyor: <b style={{ color:C.amber }}>{progress.processing}</b></span>
                <span>Biten: <b style={{ color:C.green }}>{progress.completed}</b></span>
                <span style={{ marginLeft:'auto' }}>Worker: <b style={{ color:C.sub }}>{session.concurrency}</b></span>
                {session.durationMs && <span>Süre: <b style={{ color:C.sub }}>{(session.durationMs/1000).toFixed(1)}s</b></span>}
              </div>
            </div>

            {/* Özet pills — sadece tamamlanmış oturumda */}
            {session.summary && !isActive && (
              <div style={{ display:'flex', gap:8, marginBottom:14, flexWrap:'wrap' }}>
                <Stat icon={FaCheckCircle} label="Doğrulandı" value={session.summary?.verifiedSuccess||0} c={C.green} />
                <Stat icon={FaTimesCircle} label="Başarısız" value={session.summary?.verifiedFailed||0} c={C.red} />
                <Stat icon={FaUserCheck} label="Zaten Doğru" value={session.summary?.alreadyVerified||0} c={C.cyan} />
                <Stat icon={FaArrowUp} label="Güncelleme" value={session.summary?.updateSuccess||0} c={C.purple} />
                {(session.summary?.errors||0)>0 && <Stat icon={FaExclamationTriangle} label="Hata" value={session.summary.errors} c={C.red} />}
              </div>
            )}

            {/* Tablo */}
            <div style={{ background:C.card, borderRadius:12, overflow:'hidden', border:`1px solid ${C.border}` }}>
              <div style={{ padding:'10px 14px', borderBottom:`1px solid ${C.border}`, display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                <span style={{ fontSize:12, fontWeight:700, color:C.text }}>İşlem Detayları</span>
                <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                  <SearchBox value={search} onChange={setSearch} />
                  <span style={{ fontSize:10, color:C.dim, fontWeight:600 }}>{liveLogs.length} kayıt</span>
                </div>
              </div>
              <LogTable logs={liveLogs} searchTerm={search} maxH="calc(100vh - 340px)" />
            </div>
          </>) : (
            <div style={{ background:C.card, borderRadius:12, padding:'70px 40px', border:`1px solid ${C.border}`, textAlign:'center' }}>
              <FaShieldAlt size={32} color={C.faint} style={{ marginBottom:12 }} />
              <div style={{ fontSize:14, fontWeight:700, color:C.dim }}>Aktif havuz yok</div>
              <div style={{ fontSize:11, color:C.faint, marginTop:4 }}>Yeni kayıt geldiğinde otomatik başlayacak</div>
            </div>
          )}
        </div>
      )}

      {/* ═══ GÜNLÜK ═══ */}
      {tab==='daily' && (
        <div style={{ animation:'vmFade .25s ease' }}>
          {/* Tarih + Filtre */}
          <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:14, flexWrap:'wrap', gap:8 }}>
            <div style={{ display:'flex', alignItems:'center', gap:6 }}>
              <button onClick={()=>changeDate(-1)} style={{ background:C.card, border:`1px solid ${C.border}`, color:C.text, padding:'6px 10px', borderRadius:7, cursor:'pointer', lineHeight:1 }}><FaChevronLeft size={10}/></button>
              <span style={{ fontSize:13, fontWeight:700, minWidth:140, textAlign:'center', color:C.text }}>{dayjs(date).format('DD MMMM YYYY')}</span>
              <button onClick={()=>changeDate(1)} disabled={isToday} style={{ background:C.card, border:`1px solid ${C.border}`, color:isToday?C.faint:C.text, padding:'6px 10px', borderRadius:7, cursor:isToday?'not-allowed':'pointer', opacity:isToday?.35:1, lineHeight:1 }}><FaChevronRight size={10}/></button>
              {isToday && <Pill c={C.blue}>Bugün</Pill>}
              <button onClick={()=>fetchDaily(date)} style={{ background:C.card, border:`1px solid ${C.border}`, color:C.sub, padding:'6px 10px', borderRadius:7, cursor:'pointer', lineHeight:1, marginLeft:4 }}>
                <FaSync size={10} style={refreshing?{animation:'vmSpin .8s linear infinite'}:{}} />
              </button>
            </div>
            <div style={{ display:'flex', alignItems:'center', gap:3, flexWrap:'wrap' }}>
              <FaFilter size={9} color={C.dim} style={{ marginRight:3 }} />
              {FILTERS.map(f => {
                const cnt = f.k==='all' ? dailyLogs.length : dailyLogs.filter(l=>l.status===f.k).length;
                return <FilterChip key={f.k} active={filter===f.k} label={f.l} c={f.c} count={cnt} onClick={()=>setFilter(f.k)} />;
              })}
            </div>
          </div>

          {/* Özet */}
          {ds && ds.totalCustomers>0 && (
            <div style={{ display:'flex', gap:8, marginBottom:14, flexWrap:'wrap' }}>
              <Stat icon={FaFingerprint} label="Toplam" value={ds.totalCustomers} c={C.blue} />
              <Stat icon={FaCheckCircle} label="Doğrulandı" value={ds.verifiedSuccess} c={C.green} />
              <Stat icon={FaTimesCircle} label="Başarısız" value={ds.verifiedFailed} c={C.red} />
              <Stat icon={FaUserCheck} label="Zaten Doğru" value={ds.alreadyVerified} c={C.cyan} />
              <Stat icon={FaArrowUp} label="Güncellendi" value={ds.updateSuccess} c={C.purple} />
              {ds.errors>0 && <Stat icon={FaExclamationTriangle} label="Hata" value={ds.errors} c={C.red} />}
            </div>
          )}

          {/* Tablo */}
          <div style={{ background:C.card, borderRadius:12, overflow:'hidden', border:`1px solid ${C.border}` }}>
            <div style={{ padding:'10px 14px', borderBottom:`1px solid ${C.border}`, display:'flex', justifyContent:'space-between', alignItems:'center' }}>
              <span style={{ fontSize:12, fontWeight:700, color:C.text }}>{dayjs(date).format('DD MMM')} — Kayıtlar</span>
              <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                <SearchBox value={search} onChange={setSearch} placeholder="Login veya isim..." width={160} />
                <span style={{ fontSize:10, color:C.dim, fontWeight:600 }}>{filteredLogs.length}{filter!=='all'?` / ${dailyLogs.length}`:''}</span>
              </div>
            </div>
            <LogTable logs={filteredLogs} searchTerm={search} maxH="calc(100vh - 380px)" />
          </div>
        </div>
      )}
    </div>
  );
};

export default VerificationMonitor;
